import { ButtonUtils, ObjectBuilder, Image3D, MeshUtils, Rectangle3D } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle.js";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { BroadcastManagerState, PrivateShowRefusedBy, StateChangeReason } from "@dreamcam/web-sdk";
import { text } from "../../TextResources";
import { Ellipse } from "@babylonjs/gui/2D/controls/ellipse.js";
import { AppView } from "../AppView";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { MessageType } from "../../interfaces/NotificationCenter";
import { WebXRState } from "@babylonjs/core/XR/webXRTypes.js";
import { ButtonAnimation } from "../ButtonAnimation";
import { CompatibilityOptionsImpl } from "../../settings/CompatibilityOptions";
export class BroadcastMenuModel {
    get isVisible() {
        return this._menuMesh.mesh.isVisible;
    }
    set isVisible(value) {
        if (this.isVisible === value)
            return;
        MeshUtils.setVisibility(this._menuMesh, value);
    }
    constructor(menuMesh, menuFunctions, callbacks, messageDialog, loginDialog) {
        this._isGuest = true;
        this._isFavorite = false;
        this._modelId = "";
        // canStartPrivateShow panel fields:
        this._profileButton = null;
        this._avatarCircle = null;
        this._avatarImageContainer = null;
        this._avatarImage = null;
        this._modelNickname = null;
        this._favoriteButton = null;
        this._isFavoriteButtonEnabled = true;
        // canStartSpyShow panel fields:
        this._spyShowPrice = null;
        this._privateShowRequestButtonEnabled = true;
        this._privateShowCancelRequestButtonEnabled = true;
        this._privateShowStopButtonEnabled = true;
        this._spyShowStopButtonEnabled = true;
        this._favoriteButtonHandlerAsync = async () => {
            if (!this._isFavoriteButtonEnabled || this._favoriteButton === null)
                return;
            this._isFavoriteButtonEnabled = false;
            try {
                const animatedImage = this._isGuest
                    ? this._favoriteButton.disabledImage
                    : (this._isFavorite ? this._favoriteButton.uncheckedImage : this._favoriteButton.checkedImage);
                const toggleIsFavoriteAsync = async (modelId) => {
                    if (this._isFavorite) {
                        this._updateIsFavoriteCheckbox(false, false);
                        await this._callbacks.removeModelFromFavoritesAsync(modelId);
                        this._isFavorite = false;
                    }
                    else {
                        this._updateIsFavoriteCheckbox(this._isGuest, true);
                        await this._callbacks.addModelToFavoritesAsync(modelId);
                        this._isFavorite = true;
                    }
                };
                const results = await Promise.allSettled([
                    ButtonAnimation.executeScaleAnimation(this._menuMesh.mesh.getScene(), animatedImage),
                    toggleIsFavoriteAsync(this._modelId),
                ]);
                results.forEach((x) => {
                    if (x.status === "rejected")
                        throw (x.reason);
                });
                this._callbacks.notifyViewsOnIsFavoriteChanged(this._modelId, this._isFavorite);
            }
            catch (e) {
                const re = e;
                switch (re.code) {
                    case 401: // (unauthorized)
                        this._messageDialog.openSignInMessage(this._menuMesh.mesh.position.add(new Vector3(0, 0, -0.01)), this._menuMesh.mesh.rotationQuaternion ?? this._menuMesh.mesh.rotation.toQuaternion(), async () => {
                            this._messageDialog.hide();
                            if (AppView.getInstance().isApiAuth)
                                this._loginDialog.show("signIn");
                            else
                                await AppView.getInstance().loginAsync();
                        });
                        break;
                    case 409: // (conflict: already added to favorites)
                        // ignore
                        break;
                    default:
                        console.error(re.message);
                        break;
                }
            }
            finally {
                this._updateIsFavoriteCheckbox(false, !this._isGuest && this._isFavorite);
                this._isFavoriteButtonEnabled = true;
            }
        };
        this._showBubbleMessage = (messageType, message) => {
            AppView.getInstance().appController.notificationCenter?.showMessage(messageType, message);
        };
        this._menuMesh = menuMesh;
        this._menuFunctions = menuFunctions;
        this._callbacks = callbacks;
        this._messageDialog = messageDialog;
        this._loginDialog = loginDialog;
        this.modePanel = {
            canStartPrivateShow: null,
            canCancelPrivateShowRequest: null,
            canStopPrivateShow: null,
            canStartSpyShow: null,
            canStopSpyShow: null,
        };
        this._addModelPanel();
    }
    _addModelPanel() {
        const m = Env.broadcast.menu;
        const rootContainer = new Rectangle("rootContainer");
        rootContainer.widthInPixels = this._menuMesh.m2px(m.width);
        rootContainer.heightInPixels = this._menuMesh.m2px(m.model.height);
        rootContainer.cornerRadius = Math.round(this._menuMesh.m2px(m.cornerRadius));
        rootContainer.background = m.backgroundColor;
        rootContainer.thickness = 0;
        this._menuMesh.texture.addControl(rootContainer);
        const header = this._addModelPanelHeader();
        rootContainer.addControl(header);
        this.modePanel.canStartPrivateShow = this._addCanStartPrivateShowPanel();
        rootContainer.addControl(this.modePanel.canStartPrivateShow);
        this.modePanel.canCancelPrivateShowRequest = this._addCanCancelPrivateShowRequestPanel();
        rootContainer.addControl(this.modePanel.canCancelPrivateShowRequest);
        this.modePanel.canStopPrivateShow = this._addCanStopPrivateShowPanel();
        rootContainer.addControl(this.modePanel.canStopPrivateShow);
        this.modePanel.canStartSpyShow = this._addCanStartSpyShowPanel();
        rootContainer.addControl(this.modePanel.canStartSpyShow);
        this.modePanel.canStopSpyShow = this._addCanStopSpyShowPanel();
        rootContainer.addControl(this.modePanel.canStopSpyShow);
        this.outputActions(AppView.getInstance().appController.sdk.broadcastManager.actionsState);
    }
    _addModelPanelHeader() {
        const h = Env.broadcast.menu.model.header;
        const header = new Container("header");
        header.heightInPixels = this._menuMesh.m2px(h.height);
        header.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        header.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        this._profileButton = ButtonUtils.CreateImgToggleButton("modelProfileButton", h.profileButton.colorLayout, this._menuMesh);
        this._profileButton.onIsToggledChangedObservable.add((v) => {
            this._menuFunctions.setModelProfilePanelVisibility(v);
            this._menuFunctions.updateModelProfilePanel();
            if (v) {
                this._menuFunctions.setRelatedPanelVisibility(false);
                this._menuFunctions.setHelpPanelVisibility(false);
            }
            this._menuFunctions.updateRelatedButton();
            this._menuFunctions.updateHelpButton();
            if (this._menuFunctions.getTipMenuVisibility() && v) {
                this._menuFunctions.setTipMenuVisibility(false);
                this._menuFunctions.updateTipMenuButtonLayout();
            }
        });
        header.addControl(this._profileButton);
        //--------------------------------------------------------------------------------------------------------------
        // Avatar icon:
        this._avatarCircle = new Ellipse("circle");
        this._avatarCircle.thickness = 6;
        const av = Env.broadcast.menu.model.header.profileButton.children.avatar;
        this._avatarCircle.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        this._avatarCircle.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        this._avatarCircle.widthInPixels = Math.round(this._menuMesh.m2px(av.width));
        this._avatarCircle.heightInPixels = Math.round(this._menuMesh.m2px(av.height));
        this._avatarCircle.leftInPixels = Math.round(this._menuMesh.m2px(av.left));
        this._avatarCircle.topInPixels = Math.round(this._menuMesh.m2px(av.top));
        this._avatarCircle.background = "transparent";
        const broadcastManagerStateToLayout = this._broadcastManagerStateToLayout(BroadcastManagerState.UNINITIALIZED);
        this._avatarCircle.color = broadcastManagerStateToLayout.color;
        this._profileButton.addControl(this._avatarCircle);
        this._avatarImageContainer = new Ellipse("avatarContainer");
        this._avatarImageContainer.widthInPixels = this._menuMesh.m2px(broadcastManagerStateToLayout.imageDiameter);
        this._avatarImageContainer.heightInPixels = this._menuMesh.m2px(broadcastManagerStateToLayout.imageDiameter);
        this._avatarImageContainer.clipChildren = true;
        this._avatarImageContainer.thickness = 0;
        this._avatarCircle.addControl(this._avatarImageContainer);
        this._avatarImage = new Image3D("modelPfp", Env.lobby.gallery.defaultPreview.path);
        this._avatarImage.heightInPixels = this._menuMesh.m2px(broadcastManagerStateToLayout.imageDiameter);
        this._avatarImage.onImageLoadedObservable.addOnce((image) => {
            const aspectRatio = image.domImage.width / image.domImage.height;
            this._avatarImage.widthInPixels = this._avatarImage.heightInPixels * aspectRatio;
        });
        this._avatarImageContainer.addControl(this._avatarImage);
        //--------------------------------------------------------------------------------------------------------------
        this._modelNickname = ObjectBuilder.createTextBlock3D(this._menuMesh, "modelNickname", "", h.profileButton.children.modelNickname);
        this._profileButton.addControl(this._modelNickname);
        const hintLine = ObjectBuilder.createTextBlock3D(this._menuMesh, "hintLine", text.broadcast_menu_modelPanel_viewProfile, h.profileButton.children.hintLine);
        this._profileButton.addControl(hintLine);
        {
            this._favoriteButton = ButtonUtils.CreateImgButton("favoriteButton", h.favoriteButton.colorLayout, this._menuMesh);
            this._favoriteButton.onClickObserver.add(this._favoriteButtonHandlerAsync);
            header.addControl(this._favoriteButton);
            const createChildImage = (name, src) => {
                const result = new Image3D(name, src);
                result.widthInPixels = this._menuMesh.m2px(h.favoriteButton.children.width);
                result.heightInPixels = this._menuMesh.m2px(h.favoriteButton.children.height);
                this._favoriteButton.addControl(result);
                return result;
            };
            const uncheckedImage = createChildImage("favoriteUncheckedImage", h.favoriteButton.children.uncheckedImage);
            const checkedImage = createChildImage("favoriteCheckedImage", h.favoriteButton.children.checkedImage);
            const disabledImage = createChildImage("favoriteDisabledImage", h.favoriteButton.children.disabledImage);
            this._favoriteButton.uncheckedImage = uncheckedImage;
            this._favoriteButton.checkedImage = checkedImage;
            this._favoriteButton.disabledImage = disabledImage;
        }
        return header;
    }
    _updateIsFavoriteCheckbox(disabled, checked) {
        this._favoriteButton.disabledImage.isVisible = disabled;
        this._favoriteButton.uncheckedImage.isVisible = !disabled && !checked;
        this._favoriteButton.checkedImage.isVisible = !disabled && checked;
    }
    _addCanStartPrivateShowPanel() {
        const bo = Env.broadcast.menu.model.body;
        const pa = bo.canStartPrivateShow;
        // noinspection DuplicatedCode
        const body = new Container("body");
        body.isVisible = false;
        body.heightInPixels = this._menuMesh.m2px(bo.height);
        body.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        body.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        const startPrivateShowButton = ButtonUtils.CreateImgButtonWithText("startPrivateShow", {
            text: text.broadcast_menu_privateShow_enterPrivateShowButton_buttonText,
            imageLayout: pa.startPrivateShowButton.rectangleLayout,
            textLayout: pa.startPrivateShowButton.textLayout,
        }, this._menuMesh);
        startPrivateShowButton.onClickObserver.add(() => {
            this._menuFunctions.setPrivateShowPanelVisibility(true);
        });
        body.addControl(startPrivateShowButton);
        return body;
    }
    _addCanCancelPrivateShowRequestPanel() {
        const bo = Env.broadcast.menu.model.body;
        const pa = bo.canCancelPrivateShowRequest;
        const body = new Container("body");
        body.isVisible = false;
        body.heightInPixels = this._menuMesh.m2px(bo.height);
        body.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        body.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        const cancelPrivateShowRequestButton = ButtonUtils.CreateImgButtonWithText("cancelPrivateShowRequest", {
            text: text.broadcast_menu_privateShow_canCancelPrivateShowRequestPanel_cancelPrivateShowRequestButton,
            imageLayout: pa.cancelPrivateShowRequestButton.rectangleLayout,
            textLayout: pa.cancelPrivateShowRequestButton.textLayout,
        }, this._menuMesh);
        cancelPrivateShowRequestButton.onClickObserver.add(async () => {
            if (!this._privateShowCancelRequestButtonEnabled)
                return;
            this._privateShowCancelRequestButtonEnabled = false;
            try {
                await this._callbacks.cancelPrivateShowRequestAsync();
            }
            finally {
                this._privateShowCancelRequestButtonEnabled = true;
            }
        });
        body.addControl(cancelPrivateShowRequestButton);
        return body;
    }
    _addCanStopPrivateShowPanel() {
        const bo = Env.broadcast.menu.model.body;
        const pa = bo.canStopPrivateShow;
        const body = new Container("body");
        body.isVisible = false;
        body.heightInPixels = this._menuMesh.m2px(bo.height);
        body.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        body.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        const stopPrivateShowButton = ButtonUtils.CreateImgButtonWithText("stopPrivateShowButton", {
            text: text.broadcast_menu_privateShow_canStopPrivateShowPanel_stopPrivateShowButton,
            imageLayout: pa.stopPrivateShowButton.rectangleLayout,
            textLayout: pa.stopPrivateShowButton.textLayout,
        }, this._menuMesh);
        stopPrivateShowButton.onClickObserver.add(async () => {
            if (!this._privateShowStopButtonEnabled)
                return;
            this._privateShowStopButtonEnabled = false;
            try {
                this._messageDialog.openConfirmLeaveMessage(this._menuMesh.mesh.position.add(new Vector3(0, 0, -0.01)), this._menuMesh.mesh.rotationQuaternion ?? this._menuMesh.mesh.rotation.toQuaternion(), async () => {
                    this._messageDialog.hide();
                    await this._callbacks.leavePrivateShowAsync();
                });
            }
            finally {
                this._privateShowStopButtonEnabled = true;
            }
        });
        body.addControl(stopPrivateShowButton);
        return body;
    }
    _addCanStartSpyShowPanel() {
        const bo = Env.broadcast.menu.model.body;
        const pa = bo.canStartSpyShow;
        // noinspection DuplicatedCode
        const body = new Container("body");
        body.isVisible = false;
        body.heightInPixels = this._menuMesh.m2px(bo.height);
        body.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        body.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        const infoPanel = new Rectangle3D("infoPanel");
        infoPanel.widthInPixels = this._menuMesh.m2px(pa.info.width);
        infoPanel.heightInPixels = this._menuMesh.m2px(pa.info.height);
        infoPanel.leftInPixels = this._menuMesh.m2px(pa.info.left);
        infoPanel.topInPixels = this._menuMesh.m2px(pa.info.top);
        infoPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        infoPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        infoPanel.background = pa.info.background;
        infoPanel.cornerRadius = this._menuMesh.m2px(pa.info.cornerRadius);
        infoPanel.thickness = 0;
        body.addControl(infoPanel);
        const infoText = ObjectBuilder.createTextBlock3D(this._menuMesh, "text", text.broadcast_menu_spyMode_unavailableInfoLine1, pa.info.text);
        infoPanel.addControl(infoText);
        this._spyShowPrice = ObjectBuilder.createTextBlock3D(this._menuMesh, "price", "", pa.info.price);
        infoPanel.addControl(this._spyShowPrice);
        const currencyImage = new Image3D("currency", pa.info.currency.src);
        currencyImage.widthInPixels = this._menuMesh.m2px(pa.info.currency.width);
        currencyImage.heightInPixels = this._menuMesh.m2px(pa.info.currency.height);
        currencyImage.leftInPixels = this._menuMesh.m2px(pa.info.currency.left);
        currencyImage.topInPixels = this._menuMesh.m2px(pa.info.currency.top);
        currencyImage.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        currencyImage.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        infoPanel.addControl(currencyImage);
        const priceDescription = ObjectBuilder.createTextBlock3D(this._menuMesh, "text", text.broadcast_menu_spyMode_availableInfoLine3, pa.info.priceDescription);
        infoPanel.addControl(priceDescription);
        const startSpyShowButton = ButtonUtils.CreateImgButtonWithText("startSpyShowButton", {
            text: text.broadcast_menu_spyMode_enterSpyModeButton,
            imageLayout: pa.info.startSpyShowButton.rectangleLayout,
            textLayout: pa.info.startSpyShowButton.textLayout,
        }, this._menuMesh);
        startSpyShowButton.onClickObserver.add(async () => {
            await this._callbacks.enterSpyModeAsync();
        });
        infoPanel.addControl(startSpyShowButton);
        return body;
    }
    _addCanStopSpyShowPanel() {
        const bo = Env.broadcast.menu.model.body;
        const pa = bo.canStopSpyShow;
        const body = new Container("body");
        body.isVisible = false;
        body.heightInPixels = this._menuMesh.m2px(bo.height);
        body.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        body.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        const stopPrivateShowButton = ButtonUtils.CreateImgButtonWithText("stopPrivateShowButton", {
            text: text.broadcast_menu_privateShow_canStopPrivateShowPanel_stopPrivateShowButton,
            imageLayout: pa.stopSpyShowButton.rectangleLayout,
            textLayout: pa.stopSpyShowButton.textLayout,
        }, this._menuMesh);
        stopPrivateShowButton.onClickObserver.add(async () => {
            if (!this._spyShowStopButtonEnabled)
                return;
            this._spyShowStopButtonEnabled = false;
            try {
                await this._callbacks.leaveSpyModeAsync();
            }
            finally {
                this._spyShowStopButtonEnabled = true;
            }
        });
        body.addControl(stopPrivateShowButton);
        return body;
    }
    clear() {
        this.stateChanged(BroadcastManagerState.OFFLINE, true, StateChangeReason.NONE);
        this.outputModelNickname("", "");
        // this.outputSpyModeEnabled(true);
        this.outputSpyModeTokensPerMin(0);
    }
    outputModelNickname(id, text) {
        this._modelId = id;
        this._modelNickname.text = text;
    }
    outputActions(data) {
        this.modePanel.canStartPrivateShow.isVisible = data.canUseRequestPrivateShow || data.canUseRequestVoicePrivateShow;
        this.modePanel.canCancelPrivateShowRequest.isVisible = data.canUseCancelPrivateShowRequest;
        this.modePanel.canStopPrivateShow.isVisible = data.canUseCancelPrivateShow;
        this.modePanel.canStartSpyShow.isVisible = data.canUseEnterSpyShow;
        this.modePanel.canStopSpyShow.isVisible = data.canUseLeaveSpyShow;
    }
    outputSpyModeTokensPerMin(value) {
        this._spyShowPrice.text = value.toLocaleString("ru", {});
    }
    outputIsFavorite(value) {
        this._isFavorite = value;
        this._updateIsFavoriteCheckbox(false, !this._isGuest && this._isFavorite);
    }
    async sendPrivateShowRequestAsync(isAudio = false) {
        if (this._isGuest) {
            this._showBubbleMessage(MessageType.info, text.broadcast_menu_warning_nonAuthorizedUser);
        }
        else {
            if (!this._privateShowRequestButtonEnabled)
                return;
            this._privateShowRequestButtonEnabled = false;
            try {
                if (isAudio) {
                    if (AppView.getInstance().xrHelper?.baseExperience.state === WebXRState.IN_XR && CompatibilityOptionsImpl.getInstance().data?.visionPro?.forceExitXROnMicRequest !== false) {
                        this._menuFunctions.setPrivateShowPanelVisibility(false);
                        if (await AppView.getInstance().requestMic()) {
                            await this._callbacks.requestAudioPrivateShowAsync();
                        }
                    }
                    else {
                        await this._callbacks.requestAudioPrivateShowAsync();
                    }
                }
                else
                    await this._callbacks.requestPrivateShowAsync();
            }
            catch (e) {
                this._showBubbleMessage(MessageType.error, e.message);
            }
            finally {
                this._privateShowRequestButtonEnabled = true;
            }
        }
    }
    showMessagePrivateShowRequestRejected(reason) {
        switch (reason) {
            case PrivateShowRefusedBy.SETTINGS_CHANGED:
            case PrivateShowRefusedBy.MODEL:
                this._showBubbleMessage(MessageType.info, text.broadcast_menu_privateShow_privateShowRefusedByModel);
                break;
            case PrivateShowRefusedBy.BILLING:
                this._showBubbleMessage(MessageType.info, text.broadcast_menu_warning_notEnoughTokens);
                break;
            case PrivateShowRefusedBy.MAINTENANCE:
                this._showBubbleMessage(MessageType.info, text.broadcast_maintenanceIsInProgress);
                break;
        }
    }
    _broadcastManagerStateToLayout(state) {
        const st = Env.broadcast.menu.model.header.profileButton.children.avatar.states;
        switch (state) {
            case BroadcastManagerState.OFFLINE:
                return st.offline;
            case BroadcastManagerState.PUBLIC:
                return st.online;
            case BroadcastManagerState.MY_PRIVATE_SHOW:
            case BroadcastManagerState.PRIVATE_SHOW:
                return st.private;
            case BroadcastManagerState.SPY_SHOW:
                return st.spy;
            case BroadcastManagerState.AWAY:
                return st.away;
        }
        return { color: "transparent", imageDiameter: 0 };
    }
    stateChanged(state, isGuest, _reason) {
        const layout = this._broadcastManagerStateToLayout(state);
        this._avatarCircle.color = layout.color;
        this._avatarImageContainer.widthInPixels = this._menuMesh.m2px(layout.imageDiameter);
        this._avatarImageContainer.heightInPixels = this._menuMesh.m2px(layout.imageDiameter);
        this._avatarImage.heightInPixels = this._menuMesh.m2px(layout.imageDiameter);
        const aspectRatio = this._avatarImage.domImage.width / this._avatarImage.domImage.height;
        this._avatarImage.widthInPixels = this._avatarImage.heightInPixels * aspectRatio;
        this._isGuest = isGuest;
        this._updateIsFavoriteCheckbox(false, !this._isGuest && this._isFavorite);
    }
    setModelProfilePhoto(url) {
        this._avatarImage.setSource(url, Env.lobby.gallery.defaultPreview.path);
    }
    updateModelProfilePanel() {
        if (this._profileButton === null)
            return;
        this._profileButton.isToggled = this._menuFunctions.getModelProfilePanelVisibility();
    }
}
